import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryClient, queryKeys } from './query-client';

// The contacts screen's data layer. ONE call — `/api/contact-book`, proxying symphony-bff's
// ListContacts — backs both the list and a contact's detail, because every entry already carries the
// person's threads and their whole record. Opening a contact is a client-side selection, not a fetch.
//
// Types are declared here rather than imported from common-lib for the same reason `lib/routines.ts`
// declares its own: importing them drags this module's type graph into the ESM-only chain that breaks
// jest in CI.

/** One outreach thread on a contact. Direction is which list it sits in, not a field. */
export interface WebOutreachThread {
  outreachId: string;
  channel?: string;
  lastMessage?: string;
  updatedAt?: string;
  waitingOnMe?: boolean;
  /** Lowercased OutreachStatus. Empty means the outreach carries none — not that it is done. */
  status?: string;
  createdAt?: string;
  /**
   * What the outreach was about, from the message metadata the assistant wrote when composing it.
   * Often EMPTY — the in-app channel writes none — so a caller falls back to the creation date.
   */
  subject?: string;
  /**
   * The thread itself, oldest first. Present only on the person-scoped read, so the list's rows
   * carry an empty array and quote `lastMessage` instead.
   */
  messages?: WebOutreachMessage[];
}

/** One message inside a thread. `fromMe` is resolved server-side against the caller's own side. */
export interface WebOutreachMessage {
  text?: string;
  sentAt?: string;
  fromMe?: boolean;
  attachments?: WebOutreachAttachment[];
  /**
   * Who wrote it, from the message's own envelope. Present where `fromMe` cannot answer: older
   * messages carry no direction, so they are attributed to the other party and would sit on the
   * wrong side of a chat view. Empty when the body carries no envelope.
   */
  senderName?: string;
}

/** A file sent with an outreach message. */
export interface WebOutreachAttachment {
  fileName?: string;
  mimeType?: string;
  url?: string;
  /** Absent whenever the sender did not know it — which is every attachment in production today. */
  size?: number | string;
}

/** One way to reach a person, already resolved to what the row should say. */
export interface WebContactChannel {
  kind?: string;
  label?: string;
  value?: string;
  preferred?: boolean;
}

/** How a person appears in a list. Cheap enough to render anywhere. */
export interface WebContactCard {
  personId?: string;
  displayName?: string;
  labels?: string[];
  lastTouchAt?: string;
  needsAttention?: boolean;
  attentionSummary?: string;
  openLoopCount?: number;
}

/** A person and everything the user has exchanged with them, list row and detail in one. */
export interface WebContact {
  contact?: WebContactCard;
  outbound?: WebOutreachThread[];
  inbound?: WebOutreachThread[];
  lastActivityAt?: string;
  waitingOnMe?: boolean;
  waitingCount?: number;
  channels?: WebContactChannel[];
  facts?: string[];
  openLoops?: string[];
  source?: string;
  cadenceDays?: number;
  recipientId?: string;
  /**
   * What this person's OWN assistant is called, off their Recipient.
   *
   * A received outreach message is written by their AGENT on their behalf, not typed by them, so
   * this is the honest attribution in the thread. Empty when they run no assistant — the common
   * case, since most counterparties are not Symphony users — and the label falls back to the person.
   */
  agentName?: string;
}

export type ContactBookState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; contacts: WebContact[] };

async function fetchContacts(search: string): Promise<WebContact[]> {
  const res = await apiFetch(`${API_BASE}/api/contact-book`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(search ? { search } : {}),
  });
  if (!res.ok) throw new Error(`contact-book: ${res.status}`);
  const data = (await res.json()) as { contacts?: WebContact[] };
  // A row with no card cannot be drawn and cannot be opened; the server should never send one, but a
  // list that silently renders a blank row is harder to diagnose than one that never shows it.
  return (data.contacts ?? []).filter((entry) => !!entry?.contact);
}

/** Warm the account's contact book into the React Query cache on app mount, so the Your Agents
 *  Contacts summary tile doesn't sit on a spinner. Best-effort; swallows errors. */
export function prefetchContactBook(): void {
  void queryClient.prefetchQuery({ queryKey: queryKeys.contactBook(''), queryFn: () => fetchContacts('') }).catch(() => {});
}

/**
 * The contact list.
 *
 * A non-empty `search` is a different question, not a filter over the same answer: it resolves
 * against the whole people book and finds people with no outreach at all, where the empty-search
 * browse only ever returns people you have exchanged with. So the two are cached separately, keyed
 * on the query.
 */
export function useContactBook(search: string): ContactBookState {
  const query = useQuery({
    queryKey: queryKeys.contactBook(search),
    queryFn: () => fetchContacts(search),
    // A search is typed a character at a time; keeping the previous list on screen while the next
    // one loads stops the whole page flashing between keystrokes.
    //
    // `keepPreviousData`, NOT v5's `placeholderData: (previous) => previous` — this app is on
    // react-query v4, where that option takes a value rather than a function and the mismatch falls
    // through every overload into an error that names `queryKey` instead.
    keepPreviousData: true,
  });

  if (query.data) return { kind: 'ready', contacts: query.data };
  if (query.error) return { kind: 'error', message: (query.error as Error).message };
  return { kind: 'loading' };
}

/** One contact by person id — a deep link, which the endpoint answers directly. */
export function useContact(personId: string | undefined): WebContact | undefined {
  const query = useQuery({
    queryKey: queryKeys.contactBookPerson(personId),
    queryFn: async () => {
      const res = await apiFetch(`${API_BASE}/api/contact-book`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ personId }),
      });
      if (!res.ok) throw new Error(`contact-book: ${res.status}`);
      const data = (await res.json()) as { contacts?: WebContact[] };
      return data.contacts?.[0];
    },
    enabled: !!personId,
  });
  return query.data ?? undefined;
}

/**
 * Add a missing email or phone to a contact.
 *
 * Goes straight to the People service (see `pages/api/contact-channel.ts` for why there is no BFF
 * hop). The contact cache is invalidated rather than patched: the server unions the new value into
 * the stored list and returns the merged person, so re-reading is both simpler and the only way to
 * be sure the screen shows what was actually saved.
 */
export async function addContactChannel(
  personId: string,
  value: { email?: string; phone?: string },
): Promise<boolean> {
  const res = await apiFetch(`${API_BASE}/api/contact-channel`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ personId, ...value }),
  });
  if (!res.ok) return false;
  await queryClient.invalidateQueries({ queryKey: queryKeys.contactBookPerson(personId) });
  await queryClient.invalidateQueries({ queryKey: queryKeys.contactBook('') });
  return true;
}
